import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from "uuid";

import Grid from '@mui/material/Grid2';

import { 
  AdventureSpread,
  Campaign, 
  GameCard, 
  CampaignDeck,
} from "@dndModels";

import ButtonBox from "@components/input/buttons/ButtonBox";
import CommonUtilities from "@utilities/common_utilities";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";

interface Props { 
  campaign: Campaign;
  campaign_deck: CampaignDeck;
  cards: GameCard[];
  disabled: boolean;
}

// const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

const position_names: { [index: number]: string } = {
  0: "The Party Gathers",
  1: "The Adventure Begins",
  2: "The Journey",
  3: "The Entrance",
  4: "Challenge 1",
  5: "Challenge 2",
  6: "Challenge 3",
  7: "The Guardian",
  8: "The Treasure"
};

export default function AdventureSpreadsInput({ 
  campaign, 
  campaign_deck,
  cards,
  disabled,
}: Props) {
  const [adventure_spread_id, setAdventureSpreadId] = useState("");
  const [open_adventure_pieces, setOpenAdventurePieces] = useState<number[]>([]);
  const [reloading, setReloading] = useState(false);
  // const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderOpenAdventurePieces = (adventure_spread: AdventureSpread) => {
    return (
      <Grid container spacing={0} direction="row">
        { open_adventure_pieces.map((index, key) => {
          const card = adventure_spread.spread.length > index ? cards.find(o => o._id === adventure_spread.spread[index]) : undefined;
          const position_name = position_names[index];
          if (card && position_name) {
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  className='header centered'
                  size={{
                    xs: 12
                  }}>{ position_name }: { card.name }</Grid>
                <Grid
                  className='centered'
                  size={{
                    xs: 12
                  }}>
                  <img src={ card.image_url } alt={ card.name } />
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>Event: { [0,2,7].includes(index) ? card.reversed_meanings.situation : card.upright_meanings.situation }</Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>Place: { [0,2,7].includes(index) ? card.reversed_meanings.place : card.upright_meanings.place }</Grid>
              </Grid>
            );
          }
          return null;
        })}
      </Grid>
    );
  }
  
  const shuffleDeck = () => {
    campaign_deck.card_ids = common_util.shuffleList(campaign_deck.card_ids) as string[];
  }

  if (adventure_spread_id === "") {
    return (
      <>
        <Grid
          size={{
            xs: 12
          }}>
          <AddButton 
            onClick={() => {
              setAdventureSpreadId("New");
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { !reloading && campaign_deck.saved_spreads.map((spread, key) => {
            const class_name = key % 2 === 1 ? "alternate" : "";
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                className={class_name}
                size={{
                  xs: 12
                }}>
                <Grid
                  onClick={() => {
                    setAdventureSpreadId(spread.id);
                  }}
                  style={{ cursor: "pointer" }}
                  size={{
                    xs: 9
                  }}>
                  { spread.spread.map(spread_card => {
                    const card = cards.find(o => o._id === spread_card);
                    if (card) {
                      return card.name;
                    }
                    return null;
                  }).join(", ")}
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <ButtonBox 
                    name="Remove"
                    disabled={disabled}
                    onClick={() => {
                      campaign_deck.saved_spreads = campaign_deck.saved_spreads.filter(o => o.id !== spread.id);
                      setAdventureSpreadId("");
                      setReloading(true);
                    }}
                  />
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </>
    );
  } else {
    let adventure_spread: AdventureSpread = {
      id: "",
      spread: []
    };
    if (adventure_spread_id === "New") {
      // Draw the first 9? cards, 
      // give them random orientations (standard or reversed)
      // Some positions of the cards go sideways so standard becomes left and reversed becomes right.
      // Different positions in the spread have different meanings.  
      adventure_spread.id = uuidv4().toString();
      const card_ids = [...campaign_deck.card_ids];
      for (let i = 0; i < 9; ++i) {
        const card_id = card_ids.splice(0, 1);
        adventure_spread.spread.push(card_id[0]);
      }
    } else if (adventure_spread_id !== "") {
      // Load the saved adventure spread
      const spread = campaign_deck.saved_spreads.find(o => o.id === adventure_spread_id);
      if (spread) {
        adventure_spread = spread;
      }
    }
    // Cobble it together graphically and putting together all the meanings, 
    // and make it available to the user.

    // 1 sideways, 2 on 1, 3 sideways, 4, 5 up, 6, 7 down, 8, 9 sideways on 8
    const card1 = adventure_spread.spread.length > 0 ? cards.find(o => o._id === adventure_spread.spread[0]) : undefined;
    const card2 = adventure_spread.spread.length > 1 ? cards.find(o => o._id === adventure_spread.spread[1]) : undefined;
    const card3 = adventure_spread.spread.length > 2 ? cards.find(o => o._id === adventure_spread.spread[2]) : undefined;
    const card4 = adventure_spread.spread.length > 3 ? cards.find(o => o._id === adventure_spread.spread[3]) : undefined;
    const card5 = adventure_spread.spread.length > 4 ? cards.find(o => o._id === adventure_spread.spread[4]) : undefined;
    const card6 = adventure_spread.spread.length > 5 ? cards.find(o => o._id === adventure_spread.spread[5]) : undefined;
    const card7 = adventure_spread.spread.length > 6 ? cards.find(o => o._id === adventure_spread.spread[6]) : undefined;
    const card8 = adventure_spread.spread.length > 7 ? cards.find(o => o._id === adventure_spread.spread[7]) : undefined;
    const card9 = adventure_spread.spread.length > 8 ? cards.find(o => o._id === adventure_spread.spread[8]) : undefined;
    return (
      <>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 3
            }}>
            <Grid
              style={{ height: "20px", marginBottom: "10px" }}
              size={{
                xs: 12
              }}></Grid>
            <Grid
              className="header centered"
              style={{ height: "150px", marginBottom: "4px" }}
              size={{
                xs: 12
              }}>
            { position_names[0] } and { position_names[1] }
            </Grid>
            <Grid
              style={{ height: "150px", marginBottom: "4px", position: "relative" }}
              className="clickable centered"
              onClick={() => {
                setOpenAdventurePieces([0,1]);
              }}
              size={{
                xs: 12
              }}>
              {/* 1 sideways, 2 on 1 */}
              { card1 &&
                <img src={ card1.image_url } alt={ card1.name }
                  style={{
                    height: "150px",
                    transform: "rotate(-90deg)",
                    position: "absolute",
                    // right: "20%"
                    // top: ""
                  }}
                />
              }
              { card2 &&
                <img src={ card2.image_url } alt={ card2.name }
                  style={{
                    height: "150px",
                    position: "absolute",
                    // right: "20%",
                    // top: ""
                  }}
                />
              }
            </Grid>
            <Grid
              style={{ height: "150px", marginBottom: "4px" }}
              size={{
                xs: 12
              }}>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 6
            }}>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 4
              }}>
              <Grid
                style={{ height: "20px", marginBottom: "10px" }}
                size={{
                  xs: 12
                }}></Grid>
              <Grid
                className="header centered"
                style={{ height: "150px", marginBottom: "4px" }}
                size={{
                  xs: 12
                }}>
                { position_names[2] }
              </Grid>
              <Grid
                style={{ height: "150px", marginBottom: "4px" }}
                className="clickable centered"
                onClick={() => {
                  setOpenAdventurePieces([2]);
                }}
                size={{
                  xs: 12
                }}>
                { card3 &&
                  <img src={ card3.image_url } alt={ card3.name }
                    style={{
                      height: "150px",
                      transform: "rotate(-90deg)",
                      position: "absolute",
                      // right: "50%"
                    }}
                  />
                }
              </Grid>
              <Grid
                style={{ height: "150px", marginBottom: "4px" }}
                size={{
                  xs: 12
                }}>
              </Grid>
            </Grid>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 8
              }}>
              <Grid
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 6
                }}>
                <Grid
                  style={{ height: "20px", marginBottom: "10px" }}
                  size={{
                    xs: 12
                  }}></Grid>
                <Grid
                  className="header centered"
                  style={{ height: "150px", marginBottom: "4px" }}
                  size={{
                    xs: 12
                  }}>
                { position_names[3] }
                </Grid>
                <Grid
                  style={{ height: "150px", marginBottom: "4px" }}
                  className="clickable centered"
                  onClick={() => {
                    setOpenAdventurePieces([3]);
                  }}
                  size={{
                    xs: 12
                  }}>
                  { card4 &&
                    <img src={ card4.image_url } alt={ card4.name }
                      style={{
                        height: "150px",
                        marginLeft: "-10px"
                      }}
                    />
                  }
                </Grid>
                <Grid
                  style={{ height: "150px", marginBottom: "4px" }}
                  size={{
                    xs: 12
                  }}>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 6
                }}>
                <Grid
                  className="header centered"
                  style={{ height: "20px", marginBottom: "10px" }}
                  size={{
                    xs: 12
                  }}>
                  Challenges
                </Grid>
                <Grid
                  style={{ height: "150px", marginBottom: "4px" }}
                  className="clickable centered"
                  onClick={() => {
                    setOpenAdventurePieces([4]);
                  }}
                  size={{
                    xs: 12
                  }}>
                  { card5 &&
                    <img src={ card5.image_url } alt={ card5.name }
                      style={{
                        height: "150px",
                        marginBottom: "4px"
                      }}
                    />
                  }
                </Grid>
                <Grid
                  style={{ height: "150px", marginBottom: "4px" }}
                  className="clickable centered"
                  onClick={() => {
                    setOpenAdventurePieces([5]);
                  }}
                  size={{
                    xs: 12
                  }}>
                  { card6 &&
                    <img src={ card6.image_url } alt={ card6.name }
                      style={{
                        height: "150px",
                      }}
                    />
                  }
                </Grid>
                <Grid
                  style={{ height: "150px", marginBottom: "4px" }}
                  className="clickable centered"
                  onClick={() => {
                    setOpenAdventurePieces([6]);
                  }}
                  size={{
                    xs: 12
                  }}>
                  { card7 &&
                    <img src={ card7.image_url } alt={ card7.name }
                      style={{
                        height: "150px",
                        marginTop: "4px",
                      }}
                    />
                  }
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 3
            }}>
            <Grid
              style={{ height: "20px", marginBottom: "10px" }}
              size={{
                xs: 12
              }}></Grid>
            <Grid
              className="header centered"
              style={{ height: "150px", marginBottom: "4px" }}
              size={{
                xs: 12
              }}>
              { position_names[7] } and { position_names[8] }
            </Grid>
            <Grid
              style={{ height: "150px", marginBottom: "4px", position: "relative" }}
              className="clickable centered"
              onClick={() => {
                setOpenAdventurePieces([7,8]);
              }}
              size={{
                xs: 12
              }}>
              {/* 8, 9 sideways on 8 */}
              { card8 &&
                <img src={ card8.image_url } alt={ card8.name }
                  style={{
                    height: "150px",
                    position: "absolute",
                    // right: "20%"
                    // top: ""
                  }}
                />
              }
              { card9 &&
                <img src={ card9.image_url } alt={ card9.name }
                  style={{
                    height: "150px",
                    transform: "rotate(-90deg)",
                    position: "absolute",
                    // right: "20%",
                    // top: ""
                  }}
                />
              }
            </Grid>
            <Grid
              style={{ height: "150px", marginBottom: "4px" }}
              size={{
                xs: 12
              }}>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">Position 1, { position_names[0] }</span>
          { card1 &&
            <>
              <span className="header">&nbsp;({ card1.name}):</span><br/>
              Place:{ card1.reversed_meanings.place }<br/>
              Event:{ card1.reversed_meanings.situation }
            </>
          }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">Position 2, { position_names[1] }</span>
          { card2 &&
            <>
              <span className="header">&nbsp;({ card2.name}):</span><br/>
              Place:{ card2.reversed_meanings.place }<br/>
              Event:{ card2.reversed_meanings.situation }
            </>
          }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">Position 3, { position_names[2] }</span>
          { card3 &&
            <>
              <span className="header">&nbsp;({ card3.name}):</span><br/>
              Place:{ card3.reversed_meanings.place }<br/>
              Event:{ card3.reversed_meanings.situation }
            </>
          }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">Position 4, { position_names[3] }</span>
          { card4 &&
            <>
              <span className="header">&nbsp;({ card4.name}):</span><br/>
              Place:{ card4.reversed_meanings.place }<br/>
              Event:{ card4.reversed_meanings.situation }
            </>
          }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">Position 5, { position_names[4] }</span>
          { card5 &&
            <>
              <span className="header">&nbsp;({ card5.name}):</span><br/>
              Place:{ card5.reversed_meanings.place }<br/>
              Event:{ card5.reversed_meanings.situation }
            </>
          }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">Position 6, { position_names[5] }</span>
          { card6 &&
            <>
              <span className="header">&nbsp;({ card6.name}):</span><br/>
              Place:{ card6.reversed_meanings.place }<br/>
              Event:{ card6.reversed_meanings.situation }
            </>
          }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">Position 7, { position_names[6] }</span>
          { card7 &&
            <>
              <span className="header">&nbsp;({ card7.name}):</span><br/>
              Place:{ card7.reversed_meanings.place }<br/>
              Event:{ card7.reversed_meanings.situation }
            </>
          }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">Position 8, { position_names[7] }</span>
          { card8 &&
            <>
              <span className="header">&nbsp;({ card8.name}):</span><br/>
              Place:{ card8.reversed_meanings.place }<br/>
              Event:{ card8.reversed_meanings.situation }
            </>
          }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">Position 9, { position_names[8] }</span>
          { card9 &&
            <>
              <span className="header">&nbsp;({ card9.name}):</span><br/>
              Place:{ card9.reversed_meanings.place }<br/>
              Event:{ card9.reversed_meanings.situation }
            </>
          }
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <ButtonBox
            name="Shuffle and Redeal"
            disabled={disabled}
            onClick={() => {
              shuffleDeck();
              setReloading(true);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          { ["","New"].includes(adventure_spread_id) ? 
            <ButtonBox
              name="Save Adventure Spread"
              disabled={ disabled }
              onClick={() => {
                campaign_deck.saved_spreads.push(adventure_spread);
                setAdventureSpreadId(adventure_spread.id);
                setReloading(true);
              }}
            />
          :
            <ButtonBox
              name="Delete Adventure Spread"
              disabled={ disabled }
              onClick={() => {
                campaign_deck.saved_spreads = campaign_deck.saved_spreads.filter(o => o.id !== adventure_spread_id);
                setAdventureSpreadId("");
                setReloading(true);
              }}
            />
          }
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <ButtonBox
            name="Back to Spreads"
            disabled={disabled}
            onClick={() => {
              setAdventureSpreadId("");
            }}
          />
        </Grid>
        <ModalOrDrawer
          open={ open_adventure_pieces.length > 0 } 
          onClose={() => {
            setOpenAdventurePieces([]);
          }}>
          { open_adventure_pieces.length > 0 && renderOpenAdventurePieces(adventure_spread) }
        </ModalOrDrawer>
      </>
    );
  }
}
